import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { FC, useState } from 'react';
import toast from 'react-hot-toast';

import { pagesApiSlice } from '../../../../store/api/pages-api/pages-api';
import { v2Theme } from '../../pages/page-builder-v2/v2-theme';

interface DeletePageDialogProps {
  pageId: number;
  slug: string;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeletePageDialog: FC<DeletePageDialogProps> = ({ onClose, onDeleted, pageId, slug }) => {
  const [inputSlug, setInputSlug] = useState('');
  const [deletePage, { isLoading }] = pagesApiSlice.useDeleteAggPageV2Mutation();

  const handleDelete = async () => {
    try {
      await deletePage(pageId).unwrap();
      onDeleted();
    } catch {
      toast.error('Failed to delete page. Please try again.');
    }
  };

  const isMatch = inputSlug === slug;

  return (
    <ThemeProvider theme={v2Theme}>
    <Dialog fullWidth maxWidth="xs" onClose={isLoading ? undefined : onClose} open>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: 'error.50',
            flexShrink: 0,
          }}>
            <WarningAmberIcon sx={{ color: 'error.main', fontSize: 20 }} />
          </Box>
          <Typography fontWeight={600} variant="subtitle1">Delete Page</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography color="text.secondary" mb={2} variant="body2">
          This action is permanent and cannot be undone. To confirm, type the page slug below.
        </Typography>

        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          mb: 2,
          borderRadius: 1,
          backgroundColor: 'error.50',
          border: '1px solid',
          borderColor: 'error.200',
        }}>
          <Typography
            color="error.main"
            fontFamily="monospace"
            fontWeight={600}
            variant="body2"
          >
            {slug}
          </Typography>
        </Box>

        <TextField
          autoFocus
          disabled={isLoading}
          error={inputSlug.length > 0 && !isMatch}
          fullWidth
          helperText={inputSlug.length > 0 && !isMatch ? 'Slug does not match' : ' '}
          onChange={(e) => setInputSlug(e.target.value)}
          placeholder={slug}
          size="small"
          value={inputSlug}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button disabled={isLoading} onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          color="error"
          disabled={!isMatch || isLoading}
          onClick={handleDelete}
          startIcon={isLoading ? <CircularProgress color="inherit" size={16} /> : <DeleteOutlineIcon />}
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};
